import { useState } from 'react';
import { Pencil } from 'lucide-react';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerBody,
  DrawerFooter,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useUpdateContract,
  type ContractDetail,
  type ContractStatus,
  type ContractRole,
  type ContractDirection,
} from '@/api/contracts';
import {
  CONTRACT_STATUS_OPTIONS,
  CONTRACT_ROLE_OPTIONS,
  CONTRACT_DIRECTION_OPTIONS,
} from '@/pages/contracts/contract-format';
import { DateInput } from '@/components/date-input';

interface EditFormState {
  contract_status: ContractStatus;
  contract_role: ContractRole;
  contract_direction: ContractDirection;
  contract_note: string;
  contract_effective_date: string;
  contract_expiry_date: string;
  contract_key: string;
  sla_enable: boolean;
  nda_enable: boolean;
  auto_renewable: boolean;
}

function fromDetail(detail: ContractDetail): EditFormState {
  return {
    contract_status: detail.contract_status,
    contract_role: detail.contract_role,
    contract_direction: detail.contract_direction,
    contract_note: detail.contract_note ?? '',
    contract_effective_date: detail.contract_effective_date?.slice(0, 10) ?? '',
    contract_expiry_date: detail.contract_expiry_date?.slice(0, 10) ?? '',
    contract_key: detail.contract_key ?? '',
    sla_enable: detail.sla_enable ?? false,
    nda_enable: detail.nda_enable ?? false,
    auto_renewable: detail.auto_renewable ?? false,
  };
}

/**
 * Edit button + Drawer for `PUT /contracts/customers/:id`. Renders an "Edit"
 * button that opens a slide-over form pre-filled from the contract detail.
 * Only fields that are safe to patch (status, role, direction, note, dates,
 * key, sla/nda flags, auto-renewable) are exposed — service lines and
 * attachments are managed separately via their respective tabs.
 */
export function ContractEditDrawer({ detail }: { detail: ContractDetail }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<EditFormState>(() => fromDetail(detail));
  const [errors, setErrors] = useState<Partial<Record<keyof EditFormState, string>>>({});

  const updateMutation = useUpdateContract(detail.contract_id);

  const setField = <K extends keyof EditFormState>(k: K, v: EditFormState[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const handleOpen = () => {
    setForm(fromDetail(detail));
    setErrors({});
    setOpen(true);
  };

  const validate = (): boolean => {
    const errs: Partial<Record<keyof EditFormState, string>> = {};
    if (!form.contract_effective_date) errs.contract_effective_date = 'Effective date is required.';
    if (
      form.contract_expiry_date &&
      form.contract_expiry_date <= form.contract_effective_date
    ) {
      errs.contract_expiry_date = 'Expiry must be after effective date.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    updateMutation.mutate(
      {
        contract: {
          contract_status: form.contract_status,
          contract_role: form.contract_role,
          contract_direction: form.contract_direction,
          contract_note: form.contract_note || undefined,
          contract_effective_date: form.contract_effective_date,
          contract_expiry_date: form.contract_expiry_date || undefined,
          contract_key: form.contract_key || null,
          sla_enable: form.sla_enable,
          nda_enable: form.nda_enable,
          auto_renewable: form.auto_renewable,
        },
      },
      {
        onSuccess: () => {
          toast.success('Contract updated');
          setOpen(false);
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : 'Failed to update contract';
          toast.error('Failed to update contract', { description: msg });
        },
      },
    );
  };

  const BOOL_OPTIONS = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  return (
    <>
      <Button size="sm" variant="secondary" startIcon={<Pencil size={13} />} onClick={handleOpen}>
        Edit
      </Button>

      <Drawer open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Edit contract</DrawerTitle>
            <DrawerDescription>
              {detail.contract_number_id || `#${detail.contract_id}`}
            </DrawerDescription>
          </DrawerHeader>

          <DrawerBody>
            <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
              <Field label="Status" error={errors.contract_status}>
                {(fp) => (
                  <Select
                    value={form.contract_status}
                    onValueChange={(v) => setField('contract_status', v as ContractStatus)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Role" error={errors.contract_role}>
                {(fp) => (
                  <Select
                    value={form.contract_role}
                    onValueChange={(v) => setField('contract_role', v as ContractRole)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_ROLE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Direction" error={errors.contract_direction}>
                {(fp) => (
                  <Select
                    value={form.contract_direction}
                    onValueChange={(v) => setField('contract_direction', v as ContractDirection)}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_DIRECTION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Effective date" required error={errors.contract_effective_date}>
                {(fp) => (
                  <DateInput
                    {...fp}
                    value={form.contract_effective_date}
                    onChange={(e) => setField('contract_effective_date', e.target.value)}
                  />
                )}
              </Field>

              <Field label="Expiry date" error={errors.contract_expiry_date}>
                {(fp) => (
                  <DateInput
                    {...fp}
                    value={form.contract_expiry_date}
                    onChange={(e) => setField('contract_expiry_date', e.target.value)}
                  />
                )}
              </Field>

              <Field label="Contract key" error={errors.contract_key}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.contract_key}
                    onChange={(e) => setField('contract_key', e.target.value)}
                    placeholder="Optional key alias"
                  />
                )}
              </Field>

              <Field label="Note" error={errors.contract_note}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={form.contract_note}
                    onChange={(e) => setField('contract_note', e.target.value)}
                    placeholder="Optional note"
                  />
                )}
              </Field>

              <Field label="SLA enabled" error={errors.sla_enable}>
                {(fp) => (
                  <Select
                    value={String(form.sla_enable)}
                    onValueChange={(v) => setField('sla_enable', v === 'true')}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="NDA enabled" error={errors.nda_enable}>
                {(fp) => (
                  <Select
                    value={String(form.nda_enable)}
                    onValueChange={(v) => setField('nda_enable', v === 'true')}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <Field label="Auto-renewable" error={errors.auto_renewable}>
                {(fp) => (
                  <Select
                    value={String(form.auto_renewable)}
                    onValueChange={(v) => setField('auto_renewable', v === 'true')}
                  >
                    <SelectTrigger {...fp}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOL_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          </DrawerBody>

          <DrawerFooter>
            <Button
              variant="secondary"
              onClick={() => setOpen(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} loading={updateMutation.isPending}>
              Save changes
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
